// WebMediaRecorderAdapter.js
// import { RecordingAdapter } from './RecordingAdapter';
import { RecordingAdapter } from '../../../local-recording/recording';

/**
 * Adapter for recording audio using the Web MediaRecorder API.
 * @extends RecordingAdapter
 */
export class WebMediaRecorderAdapter extends RecordingAdapter {
    /**
     * Constructs a new WebMediaRecorderAdapter.
     */
    constructor() {
        super();
        this.mediaRecorder = null;
        this.stream = null;
        this.recordedChunks = [];
        this._stopPromise = null;
        this._muted = false;
        this._lastBlob = null;
        this._deviceId = null;
    }

    /**
     * Picks the best supported MIME type for audio recording.
     * @returns {string} The selected MIME type or empty string to let the browser pick.
     */
    _pickMime() {
        const prefs = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/ogg'
        ];

        // Avoid optional chaining; guard accesses.
        const MR = typeof window === 'undefined' ? null : window.MediaRecorder;

        for (let i = 0; i < prefs.length; i++) {
            const mt = prefs[i];

            if (MR && typeof MR.isTypeSupported === 'function' && MR.isTypeSupported(mt)) {
                return mt;
            }
        }

        return ''; // let browser pick
    }

    /**
     * Starts recording audio from the specified microphone device.
     * @param {string} micDeviceId - The device ID of the microphone.
     * @returns {Promise<void>}
     */
    async start(micDeviceId) {
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
            return; // already recording
        }

        this._deviceId = micDeviceId === null || micDeviceId === undefined
            ? this._deviceId === null || this._deviceId === undefined ? undefined : this._deviceId
            : micDeviceId;

        // Use the helper from RecordingAdapter; returns mic-only MediaStream
        this.stream = await this._getAudioStream(this._deviceId);

        // Respect current mute state
        if (this.stream) {
            const tracks = this.stream.getAudioTracks();

            for (let t = 0; t < tracks.length; t++) {
                tracks[t].enabled = !this._muted;
            }
        }

        this.recordedChunks = [];
        const mimeType = this._pickMime();

        this.mediaRecorder = mimeType
            ? new MediaRecorder(this.stream, { mimeType })
            : new MediaRecorder(this.stream);

        const self = this;

        this.mediaRecorder.ondataavailable = function(e) {
            if (e && e.data && e.data.size > 0) {
                self.recordedChunks.push(e.data);
            }
        };
        this.mediaRecorder.onerror = function() {
            // keep it simple; surface to console

            // console.error('[MicAdapter] MediaRecorder error', e);
        };

        // Use a timeslice to flush chunks regularly
        this.mediaRecorder.start(250);
    }

    /**
     * Stops the recording and returns the recorded audio blob.
     * @returns {Promise<{blob: Blob, type: string}>}
     */
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    async stop() {
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
            throw new Error('No active mic recorder');
        }
        if (this._stopPromise) {
            return this._stopPromise;
        }

        const self = this;

        this._stopPromise = new Promise((resolve, reject) => {
            let settled = false;

            self.mediaRecorder.onstop = function() {
                try {
                    const type = (self.recordedChunks[0] && self.recordedChunks[0].type) || 'audio/webm';
                    const blob = new Blob(self.recordedChunks, { type });

                    self._lastBlob = blob;
                    self.recordedChunks = [];
                    self._cleanupStream();
                    settled = true;
                    resolve({ blob,
                        type });
                } catch (err) {
                    self._cleanupStream();
                    settled = true;
                    reject(err);
                }
            };

            self.mediaRecorder.onerror = function(e) {
                if (!settled) {
                    self._cleanupStream();
                    settled = true;
                    reject(e && (e.error || e));
                }
            };

            try {
                if (typeof self.mediaRecorder.requestData === 'function') {
                    self.mediaRecorder.requestData();
                }
            } catch {
                // ignore
            }

            self.mediaRecorder.stop();

            // Safety timeout if onstop never fires
            setTimeout(() => {
                if (!settled) {
                    try {
                        const type2 = (self.recordedChunks[0] && self.recordedChunks[0].type) || 'audio/webm';
                        const blob2 = new Blob(self.recordedChunks, { type: type2 });

                        self._lastBlob = blob2;
                        self.recordedChunks = [];
                        self._cleanupStream();
                        settled = true;
                        resolve({ blob: blob2,
                            type: type2 });
                    } catch (err2) {
                        self._cleanupStream();
                        settled = true;
                        reject(err2);
                    }
                }
            }, 1500);
        });

        return this._stopPromise.finally(() => {
            this._stopPromise = null;
        });
    }

    /**
     * Exports the last recorded audio data.
     * @returns {Promise<{blob: Blob, type: string} | null>}
     */
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    async exportRecordedData() {
        if (!this._lastBlob) {
            return null;
        }

        return { blob: this._lastBlob,
            type: this._lastBlob.type };
    }

    /**
     * Sets the muted state of the recording.
     * @param {boolean} muted - Whether to mute the recording.
     * @returns {Promise<void>}
     */
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    async setMuted(muted) {
        this._muted = Boolean(muted);
        if (this.stream) {
            const tracks = this.stream.getAudioTracks();

            for (let i = 0; i < tracks.length; i++) {
                tracks[i].enabled = !this._muted;
            }
        }
    }

    /**
     * Sets the microphone device to use for recording.
     * @param {string} micDeviceId - The device ID of the microphone.
     * @returns {Promise<void>}
     */
    // eslint-disable-next-line require-await -- async by contract: callers rely on the returned promise
    async setMicDevice(micDeviceId) {
        // Swap devices only when not recording (simplest + safest).
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
            throw new Error('Switch mic while recording is not supported. Stop first.');
        }
        this._cleanupStream();
        this._deviceId = micDeviceId;

        // Will take effect on next start()
    }

    /**
     * Cleans up the media stream and media recorder.
     * @private
     */
    _cleanupStream() {
        try {
            if (this.mediaRecorder) {
                this.mediaRecorder.ondataavailable = null;
            }
        } catch {
            // ignore
        }
        try {
            if (this.mediaRecorder) {
                this.mediaRecorder.onstop = null;
            }
        } catch {
            // ignore
        }
        try {
            if (this.mediaRecorder) {
                this.mediaRecorder.onerror = null;
            }
        } catch {
            // ignore
        }
        this.mediaRecorder = null;

        if (this.stream) {
            try {
                const tracks = this.stream.getTracks();

                for (let i = 0; i < tracks.length; i++) {
                    tracks[i].stop();
                }
            } catch {
                // ignore
            }
            this.stream = null;
        }
    }
}
